import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Button, Grid, Stack, Typography, Autocomplete, TextField, Paper, Alert, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@personal-dashboard/ui';
import { GET_EXERCISES, GET_EXERCISE_LOGS, CREATE_EXERCISE_LOG, UPDATE_EXERCISE_LOG, DELETE_EXERCISE_LOG } from '../queries';
import { DailyWorkoutLogs } from './DailyWorkoutLogs';
import { SetEditor } from './SetEditor';

interface WorkoutLoggerProps {
  selectedDateStr: string;
  setSelectedDateStr: (d: string) => void;
  onLogSaved: () => void;
}

export const WorkoutLogger: React.FC<WorkoutLoggerProps> = ({
  selectedDateStr,
  setSelectedDateStr,
  onLogSaved,
}) => {
  const { data: exData, loading: exLoading, error: exError } = useQuery(GET_EXERCISES);
  const { data: logsData, loading: logsLoading, error: logsError } = useQuery(GET_EXERCISE_LOGS, {
    variables: { date: selectedDateStr },
  });

  const [createLog] = useMutation(CREATE_EXERCISE_LOG);
  const [updateLog] = useMutation(UPDATE_EXERCISE_LOG);
  const [deleteLog] = useMutation(DELETE_EXERCISE_LOG);

  const [selectedEx, setSelectedEx] = useState<any>(null);
  const [sets, setSets] = useState([{ reps: 10, weight: '' }]);
  const [editingLogId, setEditingLogId] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const exercises = exData?.getExercises || [];
  const logs = logsData?.getExerciseLogs || [];

  const handleAddSet = () => {
    const last = sets[sets.length - 1];
    setSets([...sets, { reps: last ? last.reps : 10, weight: last ? last.weight : '' }]);
  };

  const handleUpdateSet = (index: number, key: string, val: any) => {
    setSets(sets.map((s, i) => i === index ? { ...s, [key]: val } : s));
  };

  const handleSave = () => {
    if (!selectedEx) return;
    const setsInput = sets.map(s => ({
      reps: Number(s.reps) || 0,
      weight: Number(s.weight) || 0,
      completed: true,
    }));

    const variablesInput = { exerciseId: selectedEx.id, date: selectedDateStr, sets: setsInput };

    const currentId = editingLogId;
    // Keep the exercise selected so the next log is one tap away; reset sets.
    setSets([{ reps: 10, weight: '' }]);
    setEditingLogId(null);
    setSaveStatus('saving');

    if (currentId) {
      updateLog({
        variables: { id: currentId, input: variablesInput },
        optimisticResponse: {
          updateExerciseLog: {
            __typename: 'ExerciseLog',
            id: currentId,
            date: selectedDateStr,
            exercise: {
              __typename: 'Exercise',
              id: selectedEx.id,
              name: selectedEx.name,
              category: selectedEx.category,
              equipment: selectedEx.equipment,
              isStandard: selectedEx.isStandard || false,
            },
            sets: setsInput.map((s) => ({
              __typename: 'ExerciseSet', reps: s.reps, weight: s.weight, completed: true,
            })),
          },
        },
        refetchQueries: [{ query: GET_EXERCISE_LOGS, variables: { date: selectedDateStr } }],
      }).then(() => {
        setSaveStatus('saved');
        onLogSaved();
      }).catch(() => {
        setSaveStatus('error');
      });
    } else {
      createLog({
        variables: { input: variablesInput },
        optimisticResponse: {
          createExerciseLog: {
            __typename: 'ExerciseLog',
            id: 'temp-id-' + Date.now(),
            date: selectedDateStr,
            exercise: {
              __typename: 'Exercise',
              id: selectedEx.id,
              name: selectedEx.name,
              category: selectedEx.category,
              equipment: selectedEx.equipment,
              isStandard: selectedEx.isStandard || false,
            },
            sets: setsInput.map((s) => ({
              __typename: 'ExerciseSet', reps: s.reps, weight: s.weight, completed: true,
            })),
          },
        },
        update(cache, { data }) {
          const newLog = data?.createExerciseLog;
          if (!newLog) return;
          const existing: any = cache.readQuery({
            query: GET_EXERCISE_LOGS,
            variables: { date: selectedDateStr },
          });
          if (existing) {
            cache.writeQuery({
              query: GET_EXERCISE_LOGS,
              variables: { date: selectedDateStr },
              data: { getExerciseLogs: [...existing.getExerciseLogs, newLog] },
            });
          }
        },
      }).then(() => {
        setSaveStatus('saved');
        onLogSaved();
      }).catch(() => {
        setSaveStatus('error');
      });
    }
  };

  const handleEditClick = (log: any) => {
    setEditingLogId(log.id);
    setSelectedEx(log.exercise);
    setSets(log.sets.map((s: any) => ({
      reps: s.reps, weight: s.weight,
    })));
  };

  const handleDeleteClick = (id: string) => {
    deleteLog({
      variables: { id },
      optimisticResponse: { deleteExerciseLog: true },
      update(cache) {
        const existing: any = cache.readQuery({
          query: GET_EXERCISE_LOGS,
          variables: { date: selectedDateStr },
        });
        if (existing) {
          cache.writeQuery({
            query: GET_EXERCISE_LOGS,
            variables: { date: selectedDateStr },
            data: { getExerciseLogs: existing.getExerciseLogs.filter((log: any) => log.id !== id) },
          });
        }
      },
    }).then(() => {
      onLogSaved();
    }).catch((err) => {
      console.error(err);
    });
  };

  if (exLoading) return <CircularProgress />;
  if (exError) return <Alert severity="error">Failed to load exercises: {exError.message}</Alert>;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            {editingLogId ? 'Edit Workout Log' : 'Log an Exercise'}
          </Typography>
          <Stack spacing={2}>
            <DatePicker
              label="Date"
              value={selectedDateStr}
              onChange={(newValue) => {
                if (newValue) {
                  setSelectedDateStr(newValue.format('YYYY-MM-DD'));
                }
              }}
              fullWidth
            />
            <Autocomplete
              options={exercises}
              getOptionLabel={(o) => `${o.name} (${o.category})`}
              value={selectedEx}
              onChange={(_, val) => setSelectedEx(val)}
              renderInput={(params) => (
                <TextField {...params} label="Choose Exercise" placeholder="Search exercises..." />
              )}
              fullWidth
            />
            {selectedEx && (
              <SetEditor
                sets={sets}
                onUpdateSet={handleUpdateSet}
                onAddSet={handleAddSet}
                onDeleteSet={(idx) => setSets(sets.filter((_, i) => i !== idx))}
                onSave={handleSave}
                editingLogId={editingLogId}
                onCancel={() => {
                  setEditingLogId(null);
                  setSelectedEx(null);
                  setSets([{ reps: 10, weight: '' }]);
                }}
                saveStatus={saveStatus}
              />
            )}
          </Stack>
        </Paper>
      </Grid>
      {logsError && (
        <Grid item xs={12}>
          <Alert severity="error">Failed to load logs: {logsError.message}</Alert>
        </Grid>
      )}
      <DailyWorkoutLogs
        selectedDate={selectedDateStr}
        logsLoading={logsLoading}
        logs={logs}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
      />
    </Grid>
  );
};
